import React, { FunctionComponent, useEffect, useState } from 'react';
import withStyles from 'react-jss';
import { Modal, Icon, Grid } from 'semantic-ui-react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { addMinutes, addMonths, endOfMonth, format, isAfter } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { useQuery } from 'react-apollo';
import { omit, compose, pathOr, indexBy, propOr } from 'ramda';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import Button from 'components/Button';
import FormSelectList from 'components/FormComponents/FormSelect';
import FormSelectMCCList from 'components/FormComponents/FormSelectMCCList';
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormInput from 'components/FormComponents/FormInput';
import FormTimeInput from 'components/FormComponents/FormTimeInput';
import FormTextArea from 'components/FormComponents/FormTextArea';
import { useMutation } from 'helpers/useMutation';
import { useGetMarketplaces } from 'helpers/useGetMarketplaces';
import {
  MarketplaceFunded,
  MerchantCategoryCodes,
  TermUnitsOptions,
  VenueTypes,
  CouponTypeOptions,
  checkMinimumOrExactSpend,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  buildTimeStamp,
  validateDates,
  dateIsAfterCurrentDate,
  DATE_FORMAT,
  cstToUTC,
  midnight,
  oneMinuteToMidnight,
  getCurrentCSTDateWithTime,
} from 'helpers/date';

import GET_CAMPAIGN_BUDGET from 'components/Header/components/SubHeader/components/Budgets/Budgets.queries';
import CreateCampaignModal from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/CreateCampaignModal';
import DynamicFormInput from 'components/FormComponents/DynamicFormInput';
import { Profile } from 'components/UserMgmt/UserMgmt.helpers';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';

import {
  BudgetType,
  TermUnits,
  TargetingRule,
  CouponType,
  OfferNetworkType,
  OfferScopeType,
  RedemptionType,
  SpecialVenueType,
  VenueType,
  Campaign,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import { CampaignSummary } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantCampaigns/MerchantCampaigns.helpers';
import CampaignTable from '../CampaignTable';

import CREATE_OFFER_FOR_MERCHANT from './CreateOffer.mutations';
import { GET_MERCHANT_OFFERS_RESULTS } from '../../MerchantOffers.queries';

import ThrottleSelection from '../ThrottleSelection';
import styles from './CreateOfferModal.styles';

type FormData = {
  campaignUuid: string;
  startDate: Date;
  startTime: string;
  endDate: Date;
  endTime: string;
  cashbackPercent: string;
  cashbackAmount: string;
  offerDescription: string;
  marketplaces: string[];
  campaignFeePercent: string;
  campaignFeeAmount: string;
  cashbackDailyLimit: string;
  venueType: VenueType | SpecialVenueType;
  isMarketplaceFunded: boolean;
  createdBy: string;
  merchantCategoryCodes: string[] | undefined;
  termUnits: TermUnits;
  couponType: CouponType;
  offerNetwork: OfferNetworkType;
  minimumSpendToActivate: string;
  exactSpendToActivate?: [
    {
      name: string;
    },
  ];
  offerType: OfferNetworkType;
  redemptionType?: RedemptionType;
  restrictions: string | null;
  offerKeywords?: string;
  offerScopeType?: OfferScopeType;
};

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  merchant: { merchantName: string; merchantId: string };
};

const favoritesData: { [key: string]: Partial<FormData> } = {
  qr: {
    marketplaces: ['VENMO'],
    venueType: SpecialVenueType.BRAND_VENUE,
    isMarketplaceFunded: true,
    offerNetwork: OfferNetworkType.VENMO,
    redemptionType: RedemptionType.QR_CODE,
  },
  mcc: {
    marketplaces: ['VENMO'],
    venueType: SpecialVenueType.NO_LOCATIONS,
    isMarketplaceFunded: true,
    offerNetwork: OfferNetworkType.VENMO,
    offerScopeType: OfferScopeType.MARKETPLACE,
  },
};

interface UserProfileData {
  userProfile: Profile;
}

const CreateOfferModal: FunctionComponent<Props> = ({
  classes,
  merchant: { merchantName, merchantId },
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [marketplaces, setMarketplaces] = useState<string[]>([]);
  const [isMarketplaceFunded, setIsMarketplaceFunded] = useState<
    boolean | null
  >(null);
  const [venueType, setVenueType] = useState<
    VenueType | SpecialVenueType | null
  >(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsById, setCampaignsById] = useState<
    Record<string, CampaignSummary>
  >({});
  const [termUnits, setTermUnits] = useState(TermUnits.PERCENTAGE);
  const [targetingRule, setTargetingRule] = useState<TargetingRule>();

  const { data: userData, loading: profileLoading } = useQuery<UserProfileData>(
    GET_PROFILE,
  );

  const { data, loading: campaignLoading } = useQuery(GET_CAMPAIGN_BUDGET, {
    variables: { params: { advertiserMerchantUuid: merchantId } },
  });

  useEffect(() => {
    if (profileLoading || !userData) return;
    const { givenName, familyName } = userData.userProfile;
    setUserName(`${givenName} ${familyName}`);
  }, [userData, profileLoading]);

  useEffect(() => {
    if (campaignLoading) return;

    const campaigns = propOr([], 'campaignFindBy', data) as CampaignSummary[];

    const budgetsByCampaignUuid = compose(
      indexBy((campaign) => pathOr('', ['campaign', 'uuid'], campaign)),
    )(campaigns) as Record<string, CampaignSummary>;

    setCampaignsById(budgetsByCampaignUuid);

    setCampaigns(campaigns.map((summary) => summary.campaign));
  }, [data, campaignLoading]);

  const onOfferCreationSuccess = () => setOpen(false);

  const { runMutation, loading: mutationLoading } = useMutation(
    CREATE_OFFER_FOR_MERCHANT,
    {
      awaitRefetchQueries: true,
    },
    'The offer has been successfully created.',
    undefined,
    onOfferCreationSuccess,
  );

  const {
    MarketplaceOptions,
    buildMarketplaces,
    loading: loadingMarketPlaces,
  } = useGetMarketplaces();

  const defaultValues = {
    startTime: format(midnight(), 'HH:mm'),
    endTime: format(oneMinuteToMidnight(), 'HH:mm'),
    termUnits: TermUnits.PERCENTAGE,
    couponType: CouponType.STANDARD,
    exactSpendToActivate: [{ name: '' }],
  };

  const methods = useForm<FormData>({
    mode: 'onBlur',
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    control,
    trigger,
  } = methods;

  useEffect(() => {
    if (open) {
      reset();
      setMarketplaces([]);
      setVenueType(null);
      setTermUnits(TermUnits.PERCENTAGE);
      setIsMarketplaceFunded(null);
      setValue('startTime', addMinutes(new Date(), 5));
    }
  }, [open]);

  const selectedCouponType = watch('couponType');
  const selectedTermUnits = watch('termUnits');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const selectedCampaign = watch('campaignUuid');

  useEffect(() => {
    register({ name: 'campaignUuid' }, { required: 'Campaign is required' });
    register({ name: 'marketplaces' }, { required: 'Marketplace is required' });
    register({ name: 'termUnits' }, { required: 'Offer Type is required' });
    register({ name: 'venueType' }, { required: 'Venue Type is required' });
    register({ name: 'couponType' }, { required: 'Coupon Type is required' });
    register({ name: 'offerNetwork' });
    register({ name: 'offerScopeType' });
    register({ name: 'redemptionType' });
    register({ name: 'specialVenueType' });
    register({ name: 'isMarketplaceFunded' });
    register(
      { name: 'startDate' },
      {
        required: 'Start Date is required',
        validate: {
          oneOf: () => checkDates() || 'Start Date must come before End Date',
          isAfter: (value) =>
            isAfter(
              getCurrentCSTDateWithTime(value, getValues().startTime),
              currentDate,
            ) || "Start Date can't be earlier than today",
        },
      },
    );
    register(
      { name: 'endDate' },
      {
        required: 'End Date is required',
        validate: {
          oneOf: () => checkDates() || 'End Date must come after Start Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(
              getCurrentCSTDateWithTime(value, getValues().endTime),
            ) || "End Date can't be earlier than today",
        },
      },
    );
    register({ name: 'startTime' }, { required: 'Start Time is required' });
    register({ name: 'endTime' }, { required: 'End Time is required' });
    register({ name: 'merchantCategoryCodes' });
  }, [register]);

  useEffect(() => {
    if (selectedCampaign) {
      const campaign = campaigns.find((c) => c.uuid === selectedCampaign);
      if (campaign) {
        const campaignStartDate = toDate(campaign.startDate);
        const campaignEndDate = toDate(campaign.endDate);
        if (isAfter(campaignStartDate, currentDate)) {
          setValue('startDate', campaignStartDate, { shouldDirty: true });
          setValue('endDate', campaignEndDate, { shouldDirty: true });
        }
      }
    }
  }, [selectedCampaign]);

  useEffect(() => {
    trigger('startDate');
  }, [startTime]);

  useEffect(() => {
    trigger('endDate');
  }, [endTime]);

  useEffect(() => {
    if (termUnits === TermUnits.USD) {
      setValue('couponType', CouponType.LOYALTY);
    }
  }, [termUnits]);

  const exactSpendToActivate = useWatch({
    control,
    name: 'exactSpendToActivate',
  });

  const getFirstFixedDateRangeBudgetUuid = (campaignUuid: string) =>
    campaignsById[campaignUuid]?.budgets[0]?.type ===
    BudgetType.FIXED_AMOUNT_FIXED_DATE_RANGE
      ? campaignsById[campaignUuid]?.budgets[0]?.uuid ?? undefined
      : undefined;

  const campaignHasFixedDateRangeBudgets = (campaignUuid: string) =>
    campaignsById[campaignUuid]?.budgets?.every(
      (budget) => budget.type === BudgetType.FIXED_AMOUNT_FIXED_DATE_RANGE,
    );

  const onSubmit = ({
    campaignFeePercent,
    campaignFeeAmount,
    campaignUuid,
    cashbackDailyLimit,
    cashbackPercent,
    cashbackAmount,
    endDate,
    endTime,
    isMarketplaceFunded,
    marketplaces,
    merchantCategoryCodes,
    offerDescription,
    startDate,
    startTime,
    venueType,
    termUnits,
    couponType,
    offerNetwork,
    minimumSpendToActivate,
    exactSpendToActivate,
    redemptionType,
    restrictions,
    offerKeywords,
    offerScopeType,
  }: FormData) => {
    const endTimeStamp = cstToUTC(
      buildTimeStamp(format(endDate, DATE_FORMAT), endTime),
    );
    const startTimeStamp = cstToUTC(
      buildTimeStamp(format(startDate, DATE_FORMAT), startTime),
    );
    const parsedExactSpend =
      exactSpendToActivate
        ?.filter((exactSpend) => exactSpend.name !== '')
        .map((exactSpend) => exactSpend.name)
        .map((exactSpend) => parseFloat(exactSpend)) ?? [];
    const params = {
      cashbackDailyLimit: parseFloat(cashbackDailyLimit),
      campaignFee:
        termUnits === TermUnits.PERCENTAGE
          ? parseFloat(campaignFeePercent)
          : parseFloat(campaignFeeAmount),
      campaignUuid,
      budgetUuid: getFirstFixedDateRangeBudgetUuid(campaignUuid),
      cashbackPercentage: parseFloat(cashbackPercent),
      cashbackAmount: parseFloat(cashbackAmount),
      endDate: endTimeStamp,
      marketplaceFunded: isMarketplaceFunded,
      merchantCategoryCodes,
      merchantUuid: merchantId,
      offerDescription,
      segmentMarketplace: buildMarketplaces(marketplaces),
      startDate: startTimeStamp,
      updatedBy: userName,
      venueType,
      replicateBaseSegmentForFixedDateRangeBudgets: campaignHasFixedDateRangeBudgets(
        campaignUuid,
      ),
      targetingRule,
      termUnits,
      couponType,
      offerNetwork,
      minimumSpendToActivate: parseInt(minimumSpendToActivate ?? '0'),
      exactSpendToActivate:
        parsedExactSpend.length > 0 ? parsedExactSpend : undefined,
      redemptionType,
      restrictions:
        restrictions && restrictions?.length > 0 ? restrictions : null,
      offerKeywords,
      offerScopeType,
    };

    const omitFields =
      merchantCategoryCodes === null ||
      (merchantCategoryCodes && merchantCategoryCodes[0] === '')
        ? ['merchantCategoryCodes']
        : [];

    const omitOneOf =
      termUnits === TermUnits.PERCENTAGE
        ? 'cashbackAmount'
        : 'cashbackPercentage';

    const sanitizedParams = omit([...omitFields, omitOneOf], params);

    runMutation({
      variables: {
        params: sanitizedParams,
      },
      refetchQueries: [
        {
          query: GET_MERCHANT_OFFERS_RESULTS,
          variables: { params: { merchantUuid: merchantId } },
        },
      ],
    });
  };

  const favoritesOnClick = (name: string) => () => {
    const defaults = favoritesData[name];
    const mergedDefaults = { ...defaults, ...defaultValues };
    reset(mergedDefaults);
    setIsMarketplaceFunded(mergedDefaults['isMarketplaceFunded'] as boolean);
    setMarketplaces(mergedDefaults['marketplaces'] as string[]);
    setVenueType(mergedDefaults['venueType'] as VenueType);
  };

  const checkDates = () => {
    const startDate = getValues().startDate;
    const endDate = getValues().endDate;

    if (startDate && endDate) {
      return validateDates(startDate, endDate);
    }
    return true;
  };

  const buttonClasses = classnames(classes.button, {
    [classes.disabled]: profileLoading,
  });

  const handleModalOpen = () => {
    setCurrentDate(new Date());
    setOpen(true);
  };

  const onCloseHandler = () => setOpen(false);

  const onCampaignSelectedHandler = (campaignUuid: string) => {
    setValue('campaignUuid', campaignUuid, { shouldDirty: true });
    trigger('campaignUuid');
  };

  return (
    <Modal
      size={'large'}
      onOpen={handleModalOpen}
      open={open}
      onClose={onCloseHandler}
      trigger={
        <button className={buttonClasses} disabled={profileLoading}>
          {' '}
          {!!userName ? 'Create Offer' : 'Loading...'}
        </button>
      }
      closeIcon
      className={classes.modal}
    >
      <Modal.Header className={classes.modalHeader}>
        <Icon name="envelope open outline" />
        {merchantName} - Create Offer
      </Modal.Header>

      <Modal.Content>
        <Modal.Description>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={2}>
                    <div
                      className={classnames(classes.fieldGroup, classes.label)}
                    >
                      <h3>Campaign</h3>
                    </div>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <CampaignTable
                    campaigns={Object.values(campaignsById)}
                    onCampaignSelectedHandler={onCampaignSelectedHandler}
                    selectedCampaign={selectedCampaign}
                    loading={campaignLoading}
                    name={'campaignUuid'}
                  />
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    <CreateCampaignModal classes={classes} />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    <div
                      className={classnames(classes.fieldGroup, classes.label)}
                    >
                      <h3>Offer</h3>
                    </div>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    <div className={classes.fieldGroup}>
                      <h4>Quick Add</h4>
                    </div>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Button
                      className={classes.button}
                      type="button"
                      onClick={favoritesOnClick('qr')}
                      title="QR Offer"
                    />
                    <Button
                      className={classes.button}
                      type="button"
                      onClick={favoritesOnClick('mcc')}
                      title="MCC Code Offer"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={12}>
                    <FormTextArea
                      name="offerDescription"
                      type="text"
                      placeholder={'Enter Offer Description'}
                      register={register({
                        required: 'Offer Description is required',
                      })}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    <FormDateInput
                      label="Start Date"
                      name="startDate"
                      placeholder={currentDate}
                      value={getValues().startDate}
                    />
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <FormTimeInput
                      name="startTime"
                      useCST={false}
                      label="Start Time (CST)"
                      placeholder={midnight()}
                    />
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <FormDateInput
                      name="endDate"
                      label="End Date"
                      placeholder={endOfMonth(addMonths(currentDate, 1))}
                      value={getValues('endDate') as Date}
                    />
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <FormTimeInput
                      name="endTime"
                      useCST={false}
                      label="End Time (CST)"
                      placeholder={oneMinuteToMidnight()}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    <FormSelectList
                      label="Offer Type"
                      name="termUnits"
                      dropDownOptions={TermUnitsOptions}
                      defaultValue={termUnits}
                      wrapSelection
                      onSelectChange={setTermUnits}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormSelectList
                      label="Marketplaces"
                      name="marketplaces"
                      dropDownOptions={MarketplaceOptions}
                      defaultValue={marketplaces}
                      multiple
                      wrapSelection
                      loading={loadingMarketPlaces}
                      onSelectChange={setMarketplaces}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormSelectList
                      label="Venue Type"
                      name="venueType"
                      dropDownOptions={VenueTypes}
                      defaultValue={venueType}
                      wrapSelection
                      onSelectChange={setVenueType}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormSelectList
                      label="Marketplace Funded offer"
                      name="isMarketplaceFunded"
                      dropDownOptions={MarketplaceFunded}
                      defaultValue={isMarketplaceFunded}
                      wrapSelection
                      onSelectChange={setIsMarketplaceFunded}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    {termUnits === TermUnits.PERCENTAGE ? (
                      <FormInput
                        name="cashbackPercent"
                        type="number"
                        step="any"
                        placeholder="Enter Cashback %"
                        label="Cashback %"
                        register={register({
                          required:
                            'This Cashback % is required. Value must be between 0 and 100.',
                        })}
                      />
                    ) : (
                      <FormInput
                        name="cashbackAmount"
                        type="number"
                        step="any"
                        placeholder="Enter Cashback Amount"
                        label="Cashback $"
                        register={register({
                          required: 'Cashback Amount is required',
                        })}
                      />
                    )}
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormInput
                      name="cashbackDailyLimit"
                      type="number"
                      step="any"
                      placeholder="Enter a Daily Cashback Limit"
                      label="Daily Cashback Limit $"
                      register={register({
                        required: 'This Daily Limit is required.',
                      })}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    {termUnits === TermUnits.PERCENTAGE ? (
                      <FormInput
                        name="campaignFeePercent"
                        type="number"
                        step="any"
                        placeholder="Enter Campaign Fee %"
                        label="Campaign Fee %"
                        register={register({
                          required:
                            'This Campaign Fee % is required. Value must be between 0 and 100.',
                        })}
                      />
                    ) : (
                      <FormInput
                        name="campaignFeeAmount"
                        type="number"
                        step="any"
                        placeholder="Enter Campaign Fee Amount"
                        label="Campaign Fee $"
                        register={register({
                          required: 'This Campaign Amount is required',
                        })}
                      />
                    )}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    <FormSelectList
                      label="Coupon Type"
                      name="couponType"
                      value={selectedCouponType}
                      dropDownOptions={CouponTypeOptions}
                      defaultValue={CouponType.STANDARD}
                    />
                  </Grid.Column>
                  {(selectedCouponType === CouponType.LOYALTY ||
                    selectedTermUnits === TermUnits.USD) && (
                    <>
                      <Grid.Column width={4}>
                        <FormInput
                          name="minimumSpendToActivate"
                          type="number"
                          step="0.01"
                          placeholder="Enter Minimum Spend Amount"
                          label="Minimum Spend"
                          register={register({
                            validate: {
                              oneOf: (value) =>
                                checkMinimumOrExactSpend(
                                  value,
                                  exactSpendToActivate as [{ name: string }],
                                ) ||
                                'Can only have Minimun or Exact Spend for Loyalty Coupon offers',
                            },
                            min: {
                              value: 0.01,
                              message: 'Minimun spend is 1 cent',
                            },
                          })}
                        />
                      </Grid.Column>
                      <Grid.Column width={4}>
                        <DynamicFormInput
                          name="exactSpendToActivate"
                          type="number"
                          step="0.01"
                          placeholder="Exact Spend Amount"
                          label="Exact Spend Amount"
                          appendLabel="+"
                          register={register({
                            validate: {
                              oneOf: () =>
                                checkMinimumOrExactSpend(
                                  getValues('minimumSpendToActivate'),
                                  exactSpendToActivate as [{ name: string }],
                                ) ||
                                'Can only have Minimun or Exact Spend for Loyalty Coupon offers',
                            },
                          })}
                          size="large"
                        />
                      </Grid.Column>
                    </>
                  )}
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <FormTextArea
                      name="restrictions"
                      type="text"
                      label="Resctrictions"
                      register={register()}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <FormSelectMCCList
                      label="Merchant Category Codes"
                      name="merchantCategoryCodes"
                      dropDownOptions={MerchantCategoryCodes}
                    />
                  </Grid.Column>
                </Grid.Row>
                <ThrottleSelection
                  setTargetingRule={setTargetingRule}
                  targetingRule={targetingRule}
                />
                <Grid.Row>
                  <Grid.Column width={3}>
                    <Button
                      disabled={mutationLoading}
                      type="submit"
                      title={mutationLoading ? 'Loading...' : 'Save Offer'}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </form>
          </FormProvider>
        </Modal.Description>
      </Modal.Content>
    </Modal>
  );
};

export default withStyles(styles)(CreateOfferModal);
